import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import { CART_SERVICE_ERROR, CART_SERVICE_SUCCESS, CLEAR_CART_STATE, CLEAR_ERRORS, ON_FETCH_FAILURE, ON_FETCH_START, ON_FETCH_SUCCESS, ON_INIT_CART } from '@nike/cart-constants';

/**
 * onFetchStart is an action creator that returns a synchronous action for calling
 * at the start of thunks - this lets us log some params and also update state to
 * inform ui what requests are in flight
 *
 * @param {object} params - named parameters
 * @param {string} params.service -     descriptor for service being utilized
 * @returns {object}               flux standard action with type, meta and payload
 */
export var onFetchStart = function onFetchStart(_ref) {
  var service = _ref.service,
    payload = _objectWithoutProperties(_ref, ["service"]);
  return {
    type: ON_FETCH_START,
    meta: {
      service: service
    },
    payload: payload
  };
};

/**
 * onInitCart is an action creator that allows us to persist the appId in state for later
 * use. It also logs the service and passes through params for logging in the payload.
 *
 * @param {object} params - named parameters
 * @param  {string} params.appId - appId defined by app via unite (uxid)
 * @param  {string} params.service - descriptor for service being utilized
 * @param {string} params.amount - gift card amount
 * @param {string} params.brand - brand
 * @param {string} params.channel - channel
 * @param {string} params.cloudStack - cloud_stack header to pass along to ux-tread-cart
 * @param {string} params.country - country code
 * @param {string} params.itemId - itemId is an optional param for editing an existing item in cart
 * @param {string} params.merchGroup - locale specific information {us|ap|eu}
 * @param {string} params.postalAddress1 - gift card recipient's address line 1
 * @param {string} params.postalAddress2 - gift card recipient's address line 2
 * @param {string} params.postalCity - gift card recipient's city
 * @param {string} params.postalCountry - gift card recipient's country
 * @param {string} params.postalState - gift card recipient's state
 * @param {string} params.postalZipCode - gift card recipient's zip code
 * @param {string} params.quantity - gift card quantity
 * @param {string} params.recipientEmailAddress - gift card recipient's email
 * @param {string} params.recipientFirstName - gift card recipient's first name
 * @param {string} params.recipientLastName - gift card recipient's last name
 * @param {string} params.shippingMethod - shipping method for a gift card
 * @param {string} params.token - unite authToken
 * @param {string} params.visitId - visit id
 * @param {string} params.visitorId - visitor id
 * @returns {object} flux standard action with type, meta and payload
 */
export var onInitCart = function onInitCart(_ref2) {
  var appId = _ref2.appId,
    service = _ref2.service,
    country = _ref2.country,
    merchGroup = _ref2.merchGroup,
    token = _ref2.token,
    rest = _objectWithoutProperties(_ref2, ["appId", "service", "country", "merchGroup", "token"]);
  return {
    type: ON_INIT_CART,
    meta: {
      appId: appId,
      merchGroup: merchGroup,
      service: service,
      token: token
    },
    payload: _objectSpread({
      country: country
    }, rest)
  };
};

/**
 * onFetchSuccess is an action creator that returns a generic success redux action for
 * handling successful async responses
 *
 * @param {object} params - named parameters
 * @param {string} params.service - descriptor for service being utilized
 * @param {string} params.errors - Errors
 * @param {string} params.id - Unique identifier
 * @param {Array} params.items - Items in cart
 * @param {object} params.totals - Values for all the total types, (subtotal, vasTotal...)
 * @param {Array} params.promotionCodes - Applied promo codes
 * @param {Array} params.warnings - warnings returned from service
 * @param {string} params.brand - brand returned from service
 * @param {string} params.channel - channel returned from service
 * @param {string} params.country - country returned from service
 * @param {string} params.currency - currency returned from service
 * @returns {object}                        Redux flux standard action object
 */
export var onFetchSuccess = function onFetchSuccess(_ref3) {
  var errors = _ref3.errors,
    service = _ref3.service,
    items = _ref3.items,
    totals = _ref3.totals,
    id = _ref3.id,
    _ref3$promotionCodes = _ref3.promotionCodes,
    promotionCodes = _ref3$promotionCodes === void 0 ? [] : _ref3$promotionCodes,
    warnings = _ref3.warnings,
    brand = _ref3.brand,
    channel = _ref3.channel,
    country = _ref3.country,
    currency = _ref3.currency,
    rest = _objectWithoutProperties(_ref3, ["errors", "service", "items", "totals", "id", "promotionCodes", "warnings", "brand", "channel", "country", "currency"]);
  return {
    type: CART_SERVICE_SUCCESS[service] || ON_FETCH_SUCCESS,
    meta: {
      service: service
    },
    payload: {
      errors: errors,
      warnings: warnings,
      data: _objectSpread({
        brand: brand,
        channel: channel,
        country: country,
        currency: currency,
        id: id,
        items: items,
        totals: totals,
        promotionCodes: promotionCodes
      }, rest)
    }
  };
};

/**
 * onFetchFailure is an action creator that returns a generic failure redux action for
 * error handling that returns a flux standard action type for errors (error:true, payload: error)
 *
 * @param {object} params - named parameters
 * @param {string} params.service - descriptor for service being utilized
 * @param {string} params.crcCode - CRC error code
 * @param {string} params.code - error code if one exists
 * @param {Error} params.error - an actual Error object to be assigned to payload
 * @param {object} params.data - any additional error data
 * @returns {object} Redux flux standard action object
 */
export var onFetchFailure = function onFetchFailure(_ref4) {
  var service = _ref4.service,
    error = _ref4.error,
    crcCode = _ref4.crcCode,
    data = _ref4.data,
    code = _ref4.code,
    field = _ref4.field,
    itemsRemoved = _ref4.itemsRemoved;
  return {
    type: CART_SERVICE_ERROR[service] || ON_FETCH_FAILURE,
    meta: {
      service: service
    },
    error: true,
    payload: {
      error: error,
      crcCode: crcCode,
      data: data,
      code: code,
      field: field,
      itemsRemoved: itemsRemoved
    }
  };
};
export var clearCartErrors = function clearCartErrors() {
  return {
    type: CLEAR_ERRORS
  };
};
export var clearCartState = function clearCartState() {
  return {
    type: CLEAR_CART_STATE
  };
};